import ArrowUpSend from '@react-spectrum/s2/icons/ArrowUpSend';
import Checkmark from '@react-spectrum/s2/icons/Checkmark';
import Edit from '@react-spectrum/s2/icons/Edit';
import Shuffle from '@react-spectrum/s2/icons/Shuffle';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type KeyboardEvent,
} from 'react';

import { EditableText } from '@/components/EditableText/EditableText';
import { MessageRow } from '@/spaces/chat/messages/MessageRow';
import { ThinkingIndicator } from '@/spaces/chat/messages/ThinkingIndicator';
import { STAGGER_MS } from '@/utils/animationConstants';
import { cn } from '@/utils/cn';

import { type GroundingField } from './groundingFields';
import type {
	ConversationEntry,
	GroundingReply,
	InterviewTurn,
} from './useGroundingFlow';

// What we open with. Nine questions land better when the owner knows what they are for and that
// most of the answers are already filled in.
// Long enough to read as someone composing a message, short enough not to be a wait.
const INTRO_BEAT_MS = 650;
// Matches animate-grounding-sink, so the choices are released exactly as they finish fading.
const CHOICE_EXIT_MS = 160;

// Promising a fixed number of questions was a promise we now break on purpose: one answer can
// cover several facts, so the count is not knowable up front. It also framed this as a form. What
// the owner needs to know is that we did the homework and that talking back is allowed.
const INTRO =
	"Before I build your strategy, I want to check what I know about your business. I've filled in what I could work out already, so mostly you're confirming or correcting me — and if a question doesn't make sense, just say so.";

// The interview reads as a conversation, because it is one. Same shape as the chat space: what
// we ask sits left as prose, what the owner answered sits right in a gray bubble, and the
// transcript grows as they go. Where chat has a composer, this has the answers themselves —
// three we believe, plus one to write their own.

export function GroundingStepCard({
	field,
	index,
	value,
	conversation,
	turn,
	turnLoading,
	replyPending,
	onCommit,
	onSubmitAnswer,
	onAdvance,
	onDraftChange,
	onWritingChange,
	onCancel,
	isExiting = false,
}: {
	field: GroundingField;
	index: number;
	value: string;
	/** Everything said so far, in the order it was said. */
	conversation: ConversationEntry[];
	turn: InterviewTurn | null;
	turnLoading: boolean;
	/** True while a typed answer is being read. */
	replyPending: boolean;
	onCommit: (value: string) => void;
	/**
	 * Hands a typed answer over to be READ rather than stored. Resolves to what was made of it: on
	 * a follow-up we stay on this fact, otherwise the flow advances.
	 */
	onSubmitAnswer: (value: string) => Promise<GroundingReply>;
	/** Moves to the next outstanding fact. Separate from committing, because a follow-up commits
	 * nothing and an accepted answer may have filled several facts at once. */
	onAdvance: () => void;
	/** Reports keystrokes upward so the generate pre-flight can save an uncommitted answer. */
	onDraftChange?: (value: string) => void;
	/** Reports whether the composer is open, so the panel's hint row can react. */
	onWritingChange?: (writing: boolean) => void;
	/** Leaves this step without saving it. */
	onCancel: () => void;
	/** Rendered as the outgoing half of a shuttle: no focus grab, no `grounding-step` testid. */
	isExiting?: boolean;
}) {
	// React unmounts synchronously, so a leaving element never gets to animate. The choices are
	// held for the length of their exit, then released. Holding WHICH one was picked is what lets
	// it travel up toward the transcript while the others sink away.
	const [leaving, setLeaving] = useState<string | null>(null);
	const shownRef = useRef<{ suggestions: string[]; question: string } | null>(
		null,
	);
	// The options a follow-up offered, which replace the original question's while we are still on
	// the same fact.
	const [followUp, setFollowUp] = useState<string[] | null>(null);
	const [writing, setWritingState] = useState(false);
	// Stable so the new-question effect below can depend on it without re-firing
	// on every render.
	const setWriting = useCallback(
		(next: boolean) => {
			setWritingState(next);
			onWritingChange?.(next);
		},
		[onWritingChange],
	);
	const [draft, setDraft] = useState(value);
	// The opening message arrives like any other: a thinking beat, then the bubble. Rendering it
	// instantly while the question behind it is still being composed made it read as page
	// furniture rather than as the first thing said.
	const [introSent, setIntroSent] = useState(index !== 0);
	// Once said, it stays said — the intro remains at the top of the transcript on later steps.
	const showIntro = index !== 0 || introSent;
	const inputRef = useRef<HTMLDivElement>(null);
	const endRef = useRef<HTMLDivElement>(null);

	// A new question is a new turn: collapse the writing surface so the choices lead again, and
	// drop any follow-up options — they belonged to the fact we just left.
	useEffect(() => {
		setDraft(value);
		setWriting(false);
		setFollowUp(null);
	}, [field.id, value, setWriting]);

	useEffect(() => {
		if (introSent || isExiting) return;
		const t = setTimeout(() => setIntroSent(true), INTRO_BEAT_MS);
		return () => clearTimeout(t);
	}, [introSent, isExiting]);

	useEffect(() => {
		if (!writing || isExiting) return;
		inputRef.current?.querySelector<HTMLElement>('textarea')?.focus();
	}, [writing, isExiting]);

	// Keep the newest turn in view, the way a chat does — but not on the opening turn, where the
	// intro plus the first question ARE the conversation and scrolling to the bottom would cut
	// the intro in half. Guarded because scrollIntoView is not implemented in every environment
	// we render in, and an exception here would take the whole turn down with it.
	useEffect(() => {
		if (isExiting || index === 0) return;
		endRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' });
	}, [turnLoading, writing, isExiting, index]);

	// Play the exit, then hand the answer up. The timer is the release: an animationend on a
	// grid of four can fire four times, and a dropped event would strand the choices on screen.
	const commitWithExit = (value: string) => {
		if (leaving) return;
		setLeaving(value);
		setTimeout(() => onCommit(value), CHOICE_EXIT_MS);
	};

	// A typed answer is read before it is stored, so this cannot commit-and-advance the way a
	// picked suggestion does. The composer closes immediately — their words are already in the
	// transcript, and leaving the text sitting in an open box reads as "not sent" — and the flow
	// only moves on if the answer was taken. On a follow-up we stay on this fact.
	// `typed` comes from the editor itself. TipTap binds its keymap once, at construction, so the
	// onEnter it calls is the FIRST render's closure — reading `draft` there yields the value from
	// mount, and since the editor clears on Enter regardless, the answer would be destroyed rather
	// than sent. The button has no such problem and passes nothing.
	const sendOwnAnswer = async (typed?: string) => {
		const text = (typed ?? draft).trim();
		if (!text || replyPending) return;
		setDraft('');
		setWriting(false);
		const res = await onSubmitAnswer(text);
		// They walked away from this fact while we were reading. Whatever we made of the answer
		// belongs to a question that is no longer on screen, so it changes nothing here.
		if (res.abandoned) return;
		if (res.verdict === 'question' || res.verdict === 'unclear') {
			// Staying on this fact. The follow-up brought its own easier options, so offer those
			// instead of the ones that came with the original question.
			setFollowUp(res.suggestions);
			return;
		}
		setFollowUp(null);
		onAdvance();
	};

	// A new turn's choices arrive: stop leaving so they animate in.
	useEffect(() => {
		setLeaving(null);
	}, [field.id, turn]);

	const onType = (v: string) => {
		setDraft(v);
		onDraftChange?.(v);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			// The dock closes on a window-level Escape, which would destroy the whole panel
			// mid-flow. React delegates to the portal container, so this stops the native bubble
			// before it reaches window. Back out of writing first, then out of the step.
			e.stopPropagation();
			if (writing) setWriting(false);
			else onCancel();
		}
	};

	const liveSuggestions = followUp ?? turn?.suggestions ?? [];
	const question = turn?.question ?? field.question;
	// While leaving, keep painting the choices the owner just saw; a half-faded set of the NEXT
	// question's answers would be worse than no animation at all.
	const suggestions =
		leaving && shownRef.current
			? shownRef.current.suggestions
			: liveSuggestions;
	if (!leaving && liveSuggestions.length) {
		shownRef.current = { suggestions: liveSuggestions, question };
	}
	// Two columns, so an odd number of suggestions leaves a trailing half-cell for the escape hatch
	// to complete. One suggestion is held out of that on purpose (ADO-1720): the hatch takes its own
	// row rather than sit beside a lone short answer — so that answer takes the full row too, since
	// half a row beside an empty cell is the same ragged layout either way.
	const loneSuggestion = suggestions.length === 1;
	const ownFillsRow = !loneSuggestion && suggestions.length % 2 === 1;
	// The current question is the LAST thing we said, so it is already in the conversation —
	// rendering `turn.question` again below would print it twice.
	const history = conversation;
	const lastId = history.length ? history[history.length - 1].id : '';

	return (
		<div
			data-testid={isExiting ? 'grounding-step-leaving' : 'grounding-step'}
			data-step-index={index}
			data-field={field.id}
			data-writing={writing ? 'true' : 'false'}
			className="flex h-full flex-col"
			onKeyDown={onKeyDown}
		>
			<div
				className="scrollbar-hidden flex min-h-0 flex-1 flex-col overflow-y-auto
					pr-1 [&_.max-w-\[900px\]]:max-w-full"
			>
				<div>
					{/* Opens the interview by saying why it is happening, so the first question does
				    not arrive without a reason. Its own bubble, ahead of everything else. */}
					{showIntro && <Asked text={INTRO} testId="grounding-intro" />}

					{showIntro &&
						history.map((entry) =>
							entry.role === 'agent' ? (
								<Asked
									key={entry.id}
									text={entry.text}
									// The newest thing we said IS the current question, so tests and
									// e2e can still find it by name.
									testId={
										entry.id === lastId ? 'grounding-question' : undefined
									}
									settled={entry.id !== lastId}
								/>
							) : (
								<Answered
									key={entry.id}
									text={entry.text}
									settled={entry.id !== lastId}
								/>
							),
						)}

					{/* Exactly ONE indicator at a time: it stands for whichever message is still being
				    composed — the intro, the next question, or our reply to what they just wrote.
				    Two stacked spinners is two people typing, which is not what is happening. */}
					{(!showIntro || turnLoading || replyPending) && (
						<div data-testid="grounding-thinking">
							<ThinkingIndicator />
						</div>
					)}

					<div ref={endRef} />
				</div>
			</div>

			{/* Where the chat space puts a composer, the interview puts the answers. */}
			{/* The scroll is a safety valve for a long set of choices, but its clip box is also
			    where the composer's elevated shadow and a choice's hover shadow have to land, and
			    both sit flush against the bottom edge — so both were being sheared flat. The
			    padding gives them room INSIDE the clip box (16px down clears the elevated
			    `0 4px 12px`, 12px sideways clears its blur); the matching negative margins spend
			    that room in the card's own padding, so nothing moves. */}
			<div
				className="scrollbar-hidden -mx-3 -mb-4 max-h-[62%] flex-shrink-0
					overflow-y-auto px-3 pt-3 pb-4"
			>
				{writing ? (
					<div
						ref={inputRef}
						data-testid="grounding-own-answer"
						className={cn(
							`animate-grounding-rise relative flex items-end gap-2 rounded-2xl
								p-3`,
							style({ backgroundColor: 'layer-2', boxShadow: 'elevated' }),
						)}
					>
						{/* The row bottom-aligns so the send button stays with the LAST line of a long
						    answer. That left a single line sitting 4px low, because a 24px line box
						    bottom-aligned against a 32px button cannot share its centre. Giving the
						    text the button's own height and centring the line inside it makes one
						    line read level with the button, while a taller answer still grows
						    downward from the same bottom edge. */}
						<div className="relative flex min-h-8 min-w-0 flex-1 items-center">
							<EditableText
								text={draft}
								placeholder="Type your answer…"
								// Fills the row: as a flex item it would otherwise size to its
								// content, and an EMPTY editor is 0px wide — the surface looks
								// clickable but there is nothing to click, so a cleared answer
								// can never be focused again.
								containerClassName="min-w-0 flex-1 max-h-24 overflow-auto"
								// One line, no wrapping: TipTap feeds this straight to DOMTokenList.add,
								// which rejects a token containing whitespace — a prettier line break
								// here crashes the editor on mount.
								// prettier-ignore
								editorClassName="w-full border-0 text-black caret-black text-[18px] focus:outline-none md:text-base"
								onEdit={onType}
								onEnter={(text) => {
									void sendOwnAnswer(text);
									return true;
								}}
							/>
						</div>
						<button
							type="button"
							onClick={() => void sendOwnAnswer()}
							disabled={!draft.trim() || replyPending}
							aria-label="Send answer"
							className={cn(
								`flex h-8 w-8 flex-shrink-0 items-center justify-center
									rounded-full transition-colors`,
								draft.trim()
									? 'bg-gray-900 text-white hover:bg-gray-700'
									: 'cursor-not-allowed bg-gray-100 text-gray-400',
							)}
						>
							<span
								className="flex h-[18px] w-[18px] items-center justify-center"
								style={
									{
										'--iconPrimary': draft.trim()
											? 'white'
											: 'rgb(107 114 128)',
									} as React.CSSProperties
								}
							>
								<ArrowUpSend />
							</span>
						</button>
					</div>
				) : (
					introSent &&
					!turnLoading && (
						<div
							className={cn(
								'grid grid-cols-2 gap-1.5',
								leaving && 'pointer-events-none',
							)}
						>
							{suggestions.map((s, i) => (
								<Choice
									key={s}
									text={s}
									order={i}
									testId="grounding-suggestion"
									kind={choiceKind(s, value)}
									exit={
										leaving ? (s === leaving ? 'chosen' : 'sink') : undefined
									}
									className={loneSuggestion ? 'col-span-2' : undefined}
									onPress={() => commitWithExit(s)}
								/>
							))}
							<Choice
								text={
									suggestions.length ? 'Something else' : 'Write your answer'
								}
								order={suggestions.length}
								testId="grounding-write-own"
								kind="own"
								exit={leaving ? 'sink' : undefined}
								// Fills the trailing half-cell an odd number of suggestions leaves
								// behind, and spans the row otherwise. Spanning into a row that
								// already holds a card cannot fit, so it wraps and strands an empty
								// cell above it — with the usual three suggestions that made a third
								// row (ADO-1733).
								// ONE suggestion is the exception: it has a trailing cell, but a lone
								// answer beside a two-word box read as a broken layout, so the hatch
								// takes its own full row (ADO-1720).
								className={ownFillsRow ? undefined : 'col-span-2'}
								onPress={() => setWriting(true)}
							/>
						</div>
					)
				)}
			</div>
		</div>
	);
}

/** What we asked. Left, unbubbled prose — the chat space's agent voice. */
function Asked({
	text,
	testId,
	settled = false,
}: {
	text: string;
	testId?: string;
	/** Already in the transcript: render it in place, with no entrance to replay. */
	settled?: boolean;
}) {
	return (
		<MessageRow justify="start">
			<div
				data-testid={testId}
				className={cn(
					'w-full py-1 text-sm leading-relaxed text-(--text-primary)',
					!settled && 'animate-grounding-rise',
				)}
			>
				{text}
			</div>
		</MessageRow>
	);
}

/** What the owner said. Right, in the same gray bubble the chat space uses. */
function Answered({
	text,
	settled = false,
}: {
	text: string;
	settled?: boolean;
}) {
	return (
		<MessageRow justify="end">
			<div
				data-testid="grounding-answer-bubble"
				className={cn(
					`max-w-[85%] rounded-3xl bg-gray-100 px-4 py-2.5 text-sm
					whitespace-pre-wrap text-(--text-primary)`,
					!settled && 'animate-grounding-rise',
				)}
			>
				{text}
			</div>
		</MessageRow>
	);
}

// What each option IS, which is knowable and therefore worth saying. The interview puts the
// value already on file at slot 0 (`_dedupe` in grounding_interview.py hoists it), so exactly
// one card is a confirmation and the rest are alternatives. Naming that is the difference
// between four identical rectangles and a choice with a shape.
// "Keep" rather than "On file": slot 0 is whatever we currently hold, which may itself be an
// earlier answer of the owner's ("Nothing beyond the products listed") rather than something we
// inferred. Calling that "On file" is true but reads as filing-cabinet trivia next to a real
// alternative; calling it "Keep" says what the click DOES, which is accurate either way.
const CHOICE_KINDS = {
	onFile: { label: 'Keep', Icon: Checkmark, hue: '158 64% 34%' },
	alternative: { label: 'Change to', Icon: Shuffle, hue: '221 96% 55%' },
	// Nothing on file to change FROM, so "Change to" would be answering a question we never asked.
	// Same hue and glyph as an alternative, because that is what it is — just not a replacement.
	suggested: { label: 'Suggested', Icon: Shuffle, hue: '221 96% 55%' },
	own: { label: 'My words', Icon: Edit, hue: '38 76% 34%' },
} as const;

/** What this option IS relative to what we already hold. */
function choiceKind(
	suggestion: string,
	value: string,
): keyof typeof CHOICE_KINDS {
	const held = value.trim();
	if (!held) return 'suggested';
	return suggestion.trim() === held ? 'onFile' : 'alternative';
}

function Choice({
	text,
	order,
	testId,
	kind,
	exit,
	className,
	onPress,
}: {
	text: string;
	order: number;
	testId: string;
	kind: keyof typeof CHOICE_KINDS;
	/** How this card leaves: the picked one travels up, the rest sink away. */
	exit?: 'chosen' | 'sink';
	className?: string;
	onPress: () => void;
}) {
	const { label, Icon, hue } = CHOICE_KINDS[kind];
	// Exactly ONE animation class, chosen here rather than by stacking utilities: `animation` is
	// a single shorthand, so a second utility would not layer — the one defined later in
	// index.css would silently win and the exit would never play.
	const animation = exit
		? exit === 'chosen'
			? 'animate-grounding-chosen'
			: 'animate-grounding-sink'
		: 'animate-grounding-choice';
	return (
		<button
			type="button"
			data-testid={testId}
			data-choice-kind={kind}
			onClick={onPress}
			// The answer alone names the button. Without this the eyebrow joins the accessible name
			// too ("Change to next-day delivery"), so the badge — which is a visual grouping cue,
			// not part of the answer — would be read aloud as if the owner had said it.
			aria-label={text}
			style={
				{
					// The entrance staggers; an exit must not. The delay runs up to 380ms while the
					// whole exit is released after CHOICE_EXIT_MS (160ms), so a staggered exit would
					// be unmounted before it ever painted.
					animationDelay: exit ? '0ms' : `${140 + order * STAGGER_MS}ms`,
					'--choice-hue': hue,
					// The glyph is an S2 workflow icon, which paints var(--iconPrimary) rather than
					// currentColor — so tinting it means setting the property, not a text class.
					'--iconPrimary': `hsl(${hue})`,
				} as React.CSSProperties
			}
			className={cn(
				`grounding-choice flex h-full w-full flex-col items-start gap-2
				rounded-2xl border border-(--glass-border) bg-white px-3.5 py-3
				text-left`,
				animation,
				className,
			)}
		>
			<span className="flex items-center gap-1.5" aria-hidden="true">
				<span
					className="flex h-5 w-5 flex-shrink-0 items-center justify-center
						rounded-md"
					style={{
						backgroundColor: 'hsl(var(--choice-hue) / 0.12)',
						// S2 icons have no size prop; 20x20 is their natural render, so the glyph is
						// scaled down to sit inside the tile.
						fontSize: 0,
					}}
				>
					<span className="scale-[0.6]">
						<Icon />
					</span>
				</span>
				<span
					className="text-[10px] font-semibold tracking-[0.06em] uppercase"
					style={{ color: 'hsl(var(--choice-hue))' }}
				>
					{label}
				</span>
			</span>
			{/* The answer itself, addressable on its own: the card's textContent now leads with the
			    kind badge ("Keep", "Change to"), so reading the choice off the button would hand a
			    caller "Change tonext-day delivery". */}
			{/* mt-auto so a one-line answer sits on the same baseline as the two-line answer beside
			    it — grid rows stretch to the tallest card, which otherwise left row one ragged. */}
			{/* Clamped because the value already on file is deliberately NOT length-capped
			    (ADO-1720), and a long one — a steering note, a multi-segment customer list — grew
			    its card past the panel. The row stretches to the tallest card, so mt-auto then
			    pushed the neighbour's answer below the clip and it rendered blank (ADO-1983).
			    Display only: onPress still commits the full value, so nothing is dropped. */}
			<span
				data-testid="grounding-choice-text"
				className="mt-auto line-clamp-3 text-sm leading-snug
					text-(--text-primary)"
			>
				{text}
			</span>
		</button>
	);
}
